import PropTypes from "prop-types";
import { useState } from "react";
import {
  Box,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Button,
  Avatar,
} from "@mui/material";
import { DelAPI } from "../../Components/Backend/DelAPI";
import { GetAPI } from "../../Components/Backend/GetAPI";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useMemo } from "react";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import APIAxios from "../../Api/APIAxios";
import SessaoAuth from "../../Hooks/SessaoAuth";

const URL_SERVICE = "/colab";

const ColaboradoresTabela = ({ colaboradores, setColaboradores, jornadas }) => {
  const { sessao, setSessao } = SessaoAuth();
  const setRowsPerPage = (newRowsPerPage) => {
    setSessao((prev) => ({ ...prev, rowsPerPage: newRowsPerPage }));
  };

  const jornadasMap = useMemo(() => {
    return new Map((jornadas || []).map((j) => [j.id, j])); // chave = id da jornada
  }, [jornadas]);

  const getJornadaNome = (idCalendario) => {
    const j = jornadasMap.get(idCalendario);
    return j?.descricao || "Não possui jornada definida";
  };

  const apiAxios = APIAxios();
  const [orderBy, setOrderBy] = useState("colaborador");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [sucessoAviso, setSucessoAviso] = useState(false);
  const [erroAviso, setErroAviso] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selColaborador, setSelColaborador] = useState(null);
  const [avatarUrls, setAvatarUrls] = useState({});
  const navigate = useNavigate();

  // Função para ordenar os dados
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = [...colaboradores].sort((a, b) => {
    let valA, valB;

    if (orderBy === "grupo") {
      valA = a.grupo?.nome ?? "";
      valB = b.grupo?.nome ?? "";
    } else if (orderBy === "jornada") {
      valA = getJornadaNome(a.idCalendario);
      valB = getJornadaNome(b.idCalendario);
    } else {
      valA = a[orderBy] ?? "";
      valB = b[orderBy] ?? "";
    }

    if (valA < valB) return order === "asc" ? -1 : 1;
    if (valA > valB) return order === "asc" ? 1 : -1;

    return 0;
  });

  const paginatedData = sortedData.slice(
    page * (sessao.rowsPerPage || 10),
    page * (sessao.rowsPerPage || 10) + (sessao.rowsPerPage || 10)
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para editar colaborador
  const handleEditar = (e, colaborador) => {
    e.preventDefault();
    navigate(`/colaborador/alterar/${colaborador.id}`);
  };

  const handleClickOpenExcluir = (e, colaborador) => {
    setSelColaborador(colaborador);
    setOpenExcluir(true);
  };

  const handleClickCloseExcluir = () => {
    setOpenExcluir(false);
  };

  const handleCloseAviso = (event, reason) => {
    if (reason === "clickaway") return;
    setErroAviso(false);
    setSucessoAviso(false);
  };

  const excluirColaborador = async () => {
    setLoading(true);
    setOpenExcluir(false);
    setErroAviso(false);
    setSucessoAviso(false);

    const response = await DelAPI(
      apiAxios,
      `${URL_SERVICE}/${selColaborador.id}`
    );

    setLoading(false);

    if (response.success) {
      setColaboradores((prev) =>
        prev.filter((colab) => colab.id !== selColaborador.id)
      );
      setSucessoAviso(true);
    } else {
      setErroAviso(true);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [colaboradores]);

  const carregarAvatar = async (id) => {

    const response = await GetAPI(apiAxios, '/colab/avatar/' + id,
      {
        responseType: "blob",
      });

    // Cria uma URL temporária para o blob recebido
    const blobUrl = URL.createObjectURL(response.data);

    return blobUrl;

  };

  useEffect(() => {
    let isMounted = true;

    const idsDaPagina = (paginatedData || [])
      .map((c) => c.id)
      .filter((id) => id != null);

    const idsParaBuscar = idsDaPagina.filter((id) => !avatarUrls[id]);

    if (idsParaBuscar.length === 0) return;

    (async () => {
      const results = await Promise.all(
        idsParaBuscar.map(async (id) => {
          const url = await carregarAvatar(id);
          return { id, url };
        })
      );

      if (!isMounted) return;

      setAvatarUrls((prev) => {
        const next = { ...prev };
        for (const { id, url } of results) {
          if (url) next[id] = url;
        }
        return next;
      });
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedData]);



  return (
    <Box>
      {/* Diálogo para Excluir Colaborador */}
      <Dialog
        open={openExcluir}
        onClose={handleClickCloseExcluir}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Deseja Excluir este Colaborador?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Após a exclusão, o colaborador não poderá ser recuperado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickCloseExcluir}>Cancelar</Button>
          <Button onClick={excluirColaborador} autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aviso de Exclusão com sucesso */}
      <Snackbar
        open={sucessoAviso}
        autoHideDuration={5000}
        onClose={handleCloseAviso}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // posição suspensa
      >
        <Alert
          onClose={handleCloseAviso}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Exclusão efetuada com sucesso!
        </Alert>
      </Snackbar>

      {/* Aviso de Erro de Exclusão*/}
      <Snackbar
        open={erroAviso}
        autoHideDuration={5000}
        onClose={handleCloseAviso}
        anchorOrigin={{ vertical: "top", horizontal: "center" }} // posição suspensa
      >
        <Alert
          onClose={handleCloseAviso}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Não foi possível excluir o colaborador.
        </Alert>
      </Snackbar>

      <Paper sx={{ mt: 2 }} elevation={4}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === "nome" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "nome"}
                    direction={orderBy === "nome" ? order : "asc"}
                    onClick={() => handleSort("nome")}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "cargo" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "cargo"}
                    direction={orderBy === "cargo" ? order : "asc"}
                    onClick={() => handleSort("cargo")}
                  >
                    Cargo
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "grupo" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "grupo"}
                    direction={orderBy === "grupo" ? order : "asc"}
                    onClick={() => handleSort("grupo")}
                  >
                    Grupo
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "jornada" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "jornada"}
                    direction={orderBy === "jornada" ? order : "asc"}
                    onClick={() => handleSort("jornada")}
                  >
                    Jornada
                  </TableSortLabel>
                </TableCell>

                <TableCell>{/* Ícones */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((colaborador) => (
                <TableRow key={colaborador.id}>
                  {/* Box e Avatar ao lado do nome */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar
                        src={avatarUrls[colaborador.id] || undefined}
                        sx={{ width: 32, height: 32 }}
                      />
                      {colaborador.nome}
                    </Box>
                  </TableCell>

                  <TableCell>{colaborador.cargo}</TableCell>
                  <TableCell>{colaborador.grupo.nome}</TableCell>
                  <TableCell>
                    {getJornadaNome(colaborador.idCalendario)}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "end" }}
                    >
                      <IconButton
                        aria-label="editar"
                        onClick={(event) => handleEditar(event, colaborador)}
                      >
                        <EditIcon style={{ color: "#1a3865" }} />
                      </IconButton>
                      <IconButton
                        aria-label="deletar"
                        onClick={(event) =>
                          handleClickOpenExcluir(event, colaborador)
                        }
                      >
                        <DeleteIcon style={{ color: "#f4793c" }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={colaboradores.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={sessao.rowsPerPage || 10}
          labelRowsPerPage="Linhas por página"
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            "& .MuiTablePagination-toolbar": {
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            },
            "& p": {
              marginBottom: 0,
            },
          }}
        />
      </Paper>
    </Box>
  );
};

ColaboradoresTabela.propTypes = {
  colaboradores: PropTypes.array.isRequired,
  setColaboradores: PropTypes.func.isRequired,
  jornadas: PropTypes.array,
};

export default ColaboradoresTabela;
